import { DEFAULT_LOCALE, SUPPORTED_LANGUAGES } from './common/Constants';
import { getFallbackLanguage, isNullOrUnderfined } from './common/CtxCommonAPI';
var Number_LOCALE_DATA = {
  ko: {
    delimiters: {
      thousands: ',',
      decimal: '.'
    },
    abbreviations: ['천', '만', '억', '조'],
    fileSizeUnits: ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
  },
  ru: {
    delimiters: {
      thousands: ' ',
      decimal: ','
    },
    abbreviations: [' тыс.', ' млн', ' млрд', ' трлн'],
    fileSizeUnits: ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ', 'ПБ', 'ЭБ', 'ЗБ', 'ЙБ']
  },
  'zh-CN': {
    delimiters: {
      thousands: ',',
      decimal: '.'
    },
    abbreviations: ['', '万', '亿', '万亿'],
    fileSizeUnits: ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
  },
  'zh-Hans': {
    delimiters: {
      thousands: ',',
      decimal: '.'
    },
    abbreviations: ['', '万', '亿', '万亿'],
    fileSizeUnits: ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
  },
  ja: {
    delimiters: {
      thousands: ',',
      decimal: '.'
    },
    abbreviations: ['', '万', '億', '兆'],
    fileSizeUnits: ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
  },
  de: {
    delimiters: {
      thousands: '.',
      decimal: ','
    },
    abbreviations: [' Tsd.', ' Mio.', ' Mrd.', ' Bio.'],
    fileSizeUnits: ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
  },
  nl: {
    delimiters: {
      thousands: '.',
      decimal: ','
    },
    abbreviations: ['K', ' mln.', ' mld.', ' bln.'],
    fileSizeUnits: ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
  },
  es: {
    delimiters: {
      thousands: '.',
      decimal: ','
    },
    abbreviations: [' mil', ' M', ' mil M', ' B'],
    fileSizeUnits: ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
  },
  fr: {
    delimiters: {
      thousands: ' ',
      decimal: ','
    },
    abbreviations: [' k', ' M', ' Md', ' Bn'],
    fileSizeUnits: ['o', 'Ko', 'Mo', 'Go', 'To', 'Po', 'Eo', 'Zo', 'Yo']
  },
  it: {
    delimiters: {
      thousands: '.',
      decimal: ','
    },
    abbreviations: ['', ' Mio', ' Mrd', ' Bln'],
    fileSizeUnits: ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
  },
  pt: {
    delimiters: {
      thousands: '.',
      decimal: ','
    },
    abbreviations: [' mil', ' mi', ' bi', ' tri'],
    fileSizeUnits: ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
  },
  en: {
    delimiters: {
      thousands: ',',
      decimal: '.'
    },
    abbreviations: ['K', 'M', 'B', 'T'],
    fileSizeUnits: ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
  }
};

/**
 * Compute the average value out of VALUE.
 * The other parameters are computation options.
 *
 * @param options options
 * @param options.value - value to compute
 * @param options.abbreviations - part of the language specification
 * @param options.locale  Specified locale.
 * @param options.spaceSeparated - `true` if a space must be inserted between the value and the abbreviation
 * @returns result  { value, abbreviation, unit }
 */
function computeAverage(_ref) {
  var value = _ref.value,
      abbreviations = _ref.abbreviations,
      locale = _ref.locale,
      _ref$spaceSeparated = _ref.spaceSeparated,
      spaceSeparated = _ref$spaceSeparated === void 0 ? false : _ref$spaceSeparated;
  var abbreviation = '';
  var unit = 1;
  var abs = Math.abs(value);

  if (locale.startsWith('zh') || locale === 'ja' || locale === 'ko') {
    //zh-CN, zh-Hans, zh-TW, zh-Hant, ja,ko
    if (abs >= Math.pow(10, 12)) {
      abbreviation = abbreviations[3];
      value = value / Math.pow(10, 12);
      unit = Math.pow(10, 12);
    } else if (abs < Math.pow(10, 12) && abs >= Math.pow(10, 8)) {
      abbreviation = abbreviations[2];
      value = value / Math.pow(10, 8);
      unit = Math.pow(10, 8);
    } else if (abs < Math.pow(10, 8) && abs >= Math.pow(10, 4)) {
      abbreviation = abbreviations[1];
      value = value / Math.pow(10, 4);
      unit = Math.pow(10, 4);
    } else if (locale === 'ko' && abs < Math.pow(10, 4) && abs >= Math.pow(10, 3)) {
      abbreviation = abbreviations[0];
      value = value / Math.pow(10, 3);
      unit = Math.pow(10, 3);
    }
  } else if (locale === 'es') {
    //es
    if (abs >= Math.pow(10, 12)) {
      abbreviation = abbreviations[3];
      value = value / Math.pow(10, 12);
      unit = Math.pow(10, 12);
    } else if (abs < Math.pow(10, 12) && abs >= Math.pow(10, 10)) {
      abbreviation = abbreviations[2];
      value = value / Math.pow(10, 9); // In es, 10,000,000,000 = 10 mil M, so the unit is  Math.pow(10, 9), not Math.pow(10, 10).

      unit = Math.pow(10, 9);
    } else if (abs < Math.pow(10, 10) && abs >= Math.pow(10, 6)) {
      abbreviation = abbreviations[1];
      value = value / Math.pow(10, 6);
      unit = Math.pow(10, 6);
    } else if (abs < Math.pow(10, 6) && abs >= Math.pow(10, 3)) {
      abbreviation = abbreviations[0];
      value = value / Math.pow(10, 3);
      unit = Math.pow(10, 3);
    }
  } else {
    //en, fr, pt, nl, de, it, ru
    if (abs >= Math.pow(10, 12)) {
      abbreviation = abbreviations[3];
      value = value / Math.pow(10, 12);
      unit = Math.pow(10, 12);
    } else if (abs < Math.pow(10, 12) && abs >= Math.pow(10, 9)) {
      abbreviation = abbreviations[2];
      value = value / Math.pow(10, 9);
      unit = Math.pow(10, 9);
    } else if (abs < Math.pow(10, 9) && abs >= Math.pow(10, 6)) {
      abbreviation = abbreviations[1];
      value = value / Math.pow(10, 6);
      unit = Math.pow(10, 6);
    } else if (locale !== 'it' && abs < Math.pow(10, 6) && abs >= Math.pow(10, 3)) {
      abbreviation = abbreviations[0];
      value = value / Math.pow(10, 3);
      unit = Math.pow(10, 3);
    }
  }

  var optionalSpace = spaceSeparated ? ' ' : '';

  if (abbreviation) {
    abbreviation = optionalSpace + abbreviation;
  }

  return {
    value: value,
    abbreviation: abbreviation,
    unit: unit
  };
}
/**
 * Convert a formatted number string to a unformattd number string.
 *
 * @param formattedNumStr     Formatted number string.
 * @param locale              Specified locale.
 * @returns                   Number string that converted from formatted number string.
 *                            For example, convert 1,000.23 to 1000.23
 */


function unformatNumber(formattedNumStr, locale) {
  if (!formattedNumStr) return '';
  var localeValue = locale || DEFAULT_LOCALE;
  var thousandDelimiter = getSeparator(localeValue, 'group');
  var decimalDelimiter = getSeparator(localeValue, 'decimal');
  var str = formattedNumStr.replace(new RegExp('\\' + thousandDelimiter, 'g'), '').replace(new RegExp('\\' + decimalDelimiter), '.');
  return str;
}
/**
 * Get number from formatted number string.
 *
 * @param formattedNumStr     Formatted number string.
 * @param locale              Specified locale.
 * @returns                   Number that de-localized from formatted number string.
 */


function deLocalizeNumber(formattedNumStr, locale) {
  if (!formattedNumStr) return NaN;
  var localeValue = locale || DEFAULT_LOCALE;
  var str = unformatNumber(formattedNumStr, localeValue);
  return parseFloat(str);
}

function getFractionDigits(actualFractionDigits, minFractionDigits, maximumFractionDigits) {
  if (minFractionDigits === undefined || minFractionDigits < 0) {
    minFractionDigits = actualFractionDigits;
  } //Backward compatible with old versions that doesn't have the maximumFractionDigits parameter.
  //In the old version, maximumFractionDigits and minimumFractionDigits are the same as  numberOfFractionDigits.


  if (maximumFractionDigits === undefined || maximumFractionDigits < 0) {
    maximumFractionDigits = minFractionDigits;
  } //If the numberOfFractionDigits is greater than maximumFractionDigits, the FractionDigits is the same as maximumFractionDigits


  var FractionDigits = actualFractionDigits;

  if (actualFractionDigits <= minFractionDigits) {
    FractionDigits = minFractionDigits;
  }

  if (actualFractionDigits > maximumFractionDigits) {
    FractionDigits = maximumFractionDigits;
  }

  return FractionDigits;
}
/**
 * Get formatted number string according to locale.
 *
 * @param num                 Number value or string can be parsed as number.
 * @param locale              Specified locale.
 * @param numberFormatOptions Options used to format number, for example:
 *                            {
 *                                numberOfFractionDigits:2,   //minimum fraction digits,for example 123.4 will be formatted to 123.40
 *                                maximumFractionDigits: 4;   //maximum fraction digits, for example 123.12345 will be formatted to 123.1235
 *                                shorten:false,              //if the shorten is true, the function will return short number, for example, 1000 will be formatted to be 1K in English, 1Tsd. in German
 *                                decimalSeparator: ".",      //Decimal Sperarator supports custom, for example 100.45 or 123,45
 *                                thousandSeparator: ",",     //Thousand Sperarator supports custom, for example 1,000  or  1.000
 *                            }
 * @returns                   Formatted number string according to locale.
 */


function numberWithDecimalStyle(num, locale, numberFormatOptions) {
  if (num === null || isNaN(+num)) {
    return '';
  }

  num = +num; // support for string value can be converted to number, for example: '123.45'

  var localeValue = locale || DEFAULT_LOCALE;

  var _ref2 = numberFormatOptions || {},
      numberOfFractionDigits = _ref2.numberOfFractionDigits,
      maximumFractionDigits = _ref2.maximumFractionDigits,
      _ref2$shorten = _ref2.shorten,
      shorten = _ref2$shorten === void 0 ? false : _ref2$shorten,
      thousandSeparator = _ref2.thousandSeparator,
      decimalSeparator = _ref2.decimalSeparator;

  try {
    var specifialLocale;
    var languages = Object.keys(Number_LOCALE_DATA);

    for (var _i = 0, _languages = languages; _i < _languages.length; _i++) {
      var lang = _languages[_i];

      if (localeValue.search(new RegExp('^' + lang, 'i')) !== -1) {
        specifialLocale = lang;
      }
    }

    if (specifialLocale && (shorten || thousandSeparator || decimalSeparator)) {
      return formatNumber(num, specifialLocale, {
        numberOfFractionDigits: getFractionDigits(countDigits(num), numberOfFractionDigits, maximumFractionDigits),
        shorten: shorten,
        thousandSeparator: thousandSeparator,
        decimalSeparator: decimalSeparator
      });
    } else {
      if (numberOfFractionDigits === undefined) {
        numberOfFractionDigits = 0;

        if (maximumFractionDigits === undefined) {
          maximumFractionDigits = 20;
        }
      } else {
        if (numberOfFractionDigits < 0) {
          numberOfFractionDigits = 0;
        }

        if (numberOfFractionDigits > 20) {
          numberOfFractionDigits = 20;
        }

        if (maximumFractionDigits === undefined) {
          //Backward compatible with old versions that doesn't have the maximumFractionDigits parameter.
          //In the old version, maximumFractionDigits and minimumFractionDigits are the same as  numberOfFractionDigits.
          maximumFractionDigits = numberOfFractionDigits;
        }
      }

      if (maximumFractionDigits < 0) {
        maximumFractionDigits = 0;
      }

      if (maximumFractionDigits > 20) {
        maximumFractionDigits = 20;
      }

      if (numberOfFractionDigits > maximumFractionDigits) {
        numberOfFractionDigits = 0;
      }

      return num.toLocaleString(localeValue, {
        maximumFractionDigits: maximumFractionDigits,
        minimumFractionDigits: numberOfFractionDigits
      });
    }
  } catch (err) {
    return num.toLocaleString(localeValue);
  }
}
/**
 * Format number value for zh-CN and ja.
 *
 * @param value Number value.
 * @param locale  Specified locale.
 * @param options format options
 * @param options.numberOfFractionDigits numberOfFractionDigits
 * @param options.shorten shorten
 * @param options.thousandSeparator thousandSeparator
 * @param options.decimalSeparator decimalSeparator
 * @returns formated result
 */


function formatNumber(value, locale, _ref3) {
  var numberOfFractionDigits = _ref3.numberOfFractionDigits,
      shorten = _ref3.shorten,
      thousandSeparator = _ref3.thousandSeparator,
      decimalSeparator = _ref3.decimalSeparator;

  if (value === 0 && 1 / value === -Infinity) {
    return '-0';
  }

  var abbreviation = '';
  var unit = 1;

  if (shorten) {
    var data = computeAverage({
      value: value,
      abbreviations: Number_LOCALE_DATA[locale].abbreviations,
      locale: locale
    });
    value = data.value;
    abbreviation = data.abbreviation;
    unit = data.unit;
  } //round the value to get the exact precision, for example
  //if numberOfFractionDigits =1, 9.991-->round(99.91)/10 = 10.0
  //if numberOfFractionDigits =2, 9.991-->round(999.1)/100 = 9.99


  var output = setMantissaPrecision(value, numberOfFractionDigits); //the value can be rounded to a higher range, so we need computeAverage again

  if (shorten) {
    value = Number(output) * unit;

    var _data = computeAverage({
      value: value,
      abbreviations: Number_LOCALE_DATA[locale].abbreviations,
      locale: locale
    });

    value = _data.value;
    abbreviation = _data.abbreviation;
    output = setMantissaPrecision(value, numberOfFractionDigits);
  }

  output = replaceDelimiters(output, value, true, thousandSeparator || Number_LOCALE_DATA[locale].delimiters.thousands, decimalSeparator || Number_LOCALE_DATA[locale].delimiters.decimal);

  if (shorten) {
    output = output + abbreviation;
  }

  return output;
}
/**
 * Return a string representing VALUE with a PRECISION-long mantissa.
 *
 * @param {number} value - number to precise
 * @param {number} precision - desired length for the mantissa
 * @returns {string}
 */


function toFixed(value, precision) {
  if (value.toString().indexOf('e') !== -1) {
    //TODO
    return value.toFixed(precision);
  }

  return (Math.round(+"".concat(value, "e+").concat(precision)) / Math.pow(10, precision)).toFixed(precision);
}
/**
 * Return the current OUTPUT with a mantissa precision of PRECISION.
 *
 * @param {number} value - number being currently formatted
 * @param {number} precision - desired precision of the mantissa
 * @returns {string}
 */


function setMantissaPrecision(value, precision) {
  var result = toFixed(value, precision);
  return result;
}
/**
 * Replace the decimal separator with DECIMALSEPARATOR and insert thousand
 * separators.
 *
 * @param {string} output - output being build in the process of formatting
 * @param {number} value - number being currently formatted
 * @param {boolean} thousandSeparated - `true` if the characteristic must be separated
 * @param {string} thousandSeparator - string to use as thousand separator
 * @param {string} decimalSeparator - string to use as decimal separator
 * @returns {string} result
 */


function replaceDelimiters(output, value, thousandSeparated, thousandSeparator, decimalSeparator) {
  var thousandsSize = 3;
  var result = output.toString();
  var characteristic = result.split('.')[0];
  var mantissa = result.split('.')[1];
  var hasNegativeSign = value < 0 && characteristic.indexOf('-') === 0;

  if (thousandSeparated) {
    if (hasNegativeSign) {
      // Remove the negative sign
      characteristic = characteristic.slice(1);
    }

    var indexesToInsertThousandDelimiters = indexesOfGroupSpaces(characteristic.length, thousandsSize);
    indexesToInsertThousandDelimiters.forEach(function (position, index) {
      characteristic = characteristic.slice(0, position + index) + thousandSeparator + characteristic.slice(position + index);
    });

    if (hasNegativeSign) {
      // Add back the negative sign
      characteristic = "-".concat(characteristic);
    }
  }

  if (!mantissa) {
    result = characteristic;
  } else {
    result = characteristic + decimalSeparator + mantissa;
  }

  return result;
}
/**
 * Return the indexes where are the group separations after splitting
 * Important: we start grouping from the right hand side.
 *
 * @param {number} totalLength - total length of the characteristic to split
 * @param {number} groupSize - length of each group
 * @returns {number[]} result
 */


function indexesOfGroupSpaces(totalLength, groupSize) {
  var result = [];
  var counter = 0;

  for (var i = totalLength; i > 0; i--) {
    if (counter === groupSize) {
      result.unshift(i);
      counter = 0;
    }

    counter++;
  }

  return result;
}
/**
 * Get formatted currency string according to locale.
 *
 * @param currencyValue Currency value.
 * @param currency      Currency code.
 * @param locale        Specified locale.
 * @param numberOfFractionDigits Number of fraction digits.
 * @returns        Formatted currency string according to locale.
 */


function numberWithCurrencyStyle(currencyValue, currency, locale, numberOfFractionDigits) {
  if (currencyValue === null || currency === undefined || isNaN(+currencyValue)) {
    return '';
  }

  currencyValue = +currencyValue; // support for string value can be converted to number, for example: '123.45'

  var localeValue = locale || DEFAULT_LOCALE;

  try {
    if (isNullOrUnderfined(numberOfFractionDigits) || !isNumber(numberOfFractionDigits)) {
      return currencyValue.toLocaleString(localeValue, {
        style: 'currency',
        currency: currency
      });
    } else {
      return currencyValue.toLocaleString(localeValue, {
        style: 'currency',
        currency: currency,
        maximumFractionDigits: numberOfFractionDigits,
        minimumFractionDigits: numberOfFractionDigits
      });
    }
  } catch (error) {
    return numberWithDecimalStyle(currencyValue, locale, {
      numberOfFractionDigits: numberOfFractionDigits
    });
  }
}
/**
 * Get formatted percent string according to locale.
 *
 * @param percent  Number with percent value or string can be parsed as number.
 * @param locale   Specified locale.
 * @param percentageFormatOptions Options used to format number, for example:
 *                                {
 *                                    thousandSeparator: ",",
 *                                }
 * @returns        Formatted pecent-style number string according to locale.
 */


function numberWithPercentStyle(percent, locale) {
  var percentageFormatOptions = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

  if (percent === null || isNaN(+percent)) {
    return '';
  }

  percent = +percent; // support for string value can be converted to number, for example: '123.45'

  var localeValue = locale || DEFAULT_LOCALE;
  var percentString = percent.toLocaleString(localeValue, {
    style: 'percent'
  });
  var defaultGroupgetSeparator = getSeparator(localeValue, 'group');
  var thousandSeparator = percentageFormatOptions.thousandSeparator;

  if (thousandSeparator !== undefined && thousandSeparator !== null && defaultGroupgetSeparator !== undefined) {
    // space in percentString will be changed to NARROW NO-BREAK SPACE by the javascript native interface
    // we need this convert in order to find the thousandSeparator
    if (defaultGroupgetSeparator === ' ') {
      defaultGroupgetSeparator = "\u202F";
    }

    return percentString.replace(new RegExp(escapeRegExp(defaultGroupgetSeparator), 'g'), thousandSeparator);
  } else {
    return percentString;
  }
}
/**
 * Get formatted number string according to specified locale, providing format options.
 *
 * @param num Number value
 * @param locale Specified locale
 * @param options Options to determine number components to show & fractionDigits to use & style to use.
 * @returns Formatted number string according to locale and providing options.
 */


function numberWithStyleEx(num) {
  var locale = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DEFAULT_LOCALE;
  var options = arguments.length > 2 ? arguments[2] : undefined;

  if (num === null || isNaN(+num)) {
    return '';
  }

  num = +num; // support for string value can be converted to number, for example: '123.45'

  var numberOptions = {};
  setNumberOptions(numberOptions, options);
  return num.toLocaleString(locale, numberOptions);
}
/**
 * Get formatted number string according to specified locale, minFractionDigits and maxFractionDigits.
 *
 * @param num Number value
 * @param locale Specified locale
 * @param minFractionDigits The minimum fraction digit to show. The default value is 0.
 * @param maxFractionDigits The maximum fraction digit to show. The default value is 15.
 * @returns Formatted number string according to locale, minFractionDigits and maxFractionDigits options.
 */


function numberWithCustomFractionDigits(num) {
  var locale = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DEFAULT_LOCALE;
  var minFractionDigits = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
  var maxFractionDigits = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 15;

  if (num === null || isNaN(+num)) {
    return '';
  }

  num = +num;
  var numberOptions = {
    style: 'decimal',
    maximumFractionDigits: maxFractionDigits,
    minimumFractionDigits: minFractionDigits
  };
  return num.toLocaleString(locale, numberOptions);
} // Retrieve either the decimal or group separator of a given locale


function getSeparator(locale, separatorType) {
  if (separatorType === 'group') {
    return Intl.NumberFormat(locale).formatToParts(11111)[1].value;
  } else {
    return Intl.NumberFormat(locale).formatToParts(1.1)[1].value;
  }
} // The group separator for de is '.', so we need to escape it before regexp search.


function escapeRegExp(text) {
  return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}
/**
 * Prepare number options according to input.
 *
 * @param options     Source options
 * @param dateOptions Target options
 */


function setNumberOptions(numberOptions, options) {
  if (options) {
    var numberOfFractionDigits = options.numberOfFractionDigits;

    if (!isNullOrUnderfined(numberOfFractionDigits) && isNumber(numberOfFractionDigits)) {
      numberOptions.maximumFractionDigits = numberOfFractionDigits;
      numberOptions.minimumFractionDigits = numberOfFractionDigits;
    }

    if (options.style) {
      numberOptions.style = options.style;

      if (options.currency) {
        numberOptions.currency = options.currency;
      }
    }
  }
}
/**
 * Get formatted file size string according to locale.
 *
 * @param sizeInBytes         file size of bytes.
 * @param locale              Specified locale,for examppe en-US, en, de-DE, de, etc.
 * @param numberFormatOptions Options used to format number, for example:
 *                            {
 *                                numberOfFractionDigits:2,   //the number of fraction digits,for example 123.45 has two digits
 *                                shorten:false,              //if the shorten is true, the function will return short number, for example, 1000 will be formatted to be 1K in English, 1Tsd. in German
 *                                decimalSeparator: ".",      //Decimal Sperarator supports custom, for example 100.45 or 123,45
 *                                thousandSeparator: ",",     //Thousand Sperarator supports custom, for example 1,000  or  1.000
 *                            }
 * @returns                   Formatted file size string according to locale, for example 123 B, 1,023 B, 5 KB, 1,000 GB in english or 123 o, 1.023 o, 5 Ko, 1.000 Go .
 */


function getFileSize(sizeInBytes) {
  var locale = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DEFAULT_LOCALE;
  var numberFormatOptions = arguments.length > 2 ? arguments[2] : undefined;

  if (sizeInBytes === null || isNaN(+sizeInBytes)) {
    return '';
  }

  var localeValue = getFallbackLanguage(locale, SUPPORTED_LANGUAGES);
  var byteUnitLookup = Number_LOCALE_DATA[localeValue].fileSizeUnits;
  var size = +sizeInBytes;

  if (size <= 0) {
    return '0 ' + byteUnitLookup[0];
  }

  var fractionDigits = 0;

  if (numberFormatOptions == undefined || numberFormatOptions.numberOfFractionDigits == undefined) {
    fractionDigits = countDigits(size);
  } else {
    fractionDigits = numberFormatOptions.numberOfFractionDigits;
    numberFormatOptions.numberOfFractionDigits = undefined;
  }

  var unitIndex = 0;

  while (size >= 1024) {
    unitIndex++;
    size = size / 1024;
  }

  var result = numberWithDecimalStyle(+size.toFixed(fractionDigits), localeValue, numberFormatOptions);
  return result + ' ' + byteUnitLookup[unitIndex];
}

function countDigits(num) {
  var sp = (num + '').split('e');

  if (sp.length >= 2) {
    var frontArray = sp[0].split('.');
    var frontDigits = frontArray.length >= 2 ? frontArray[1].length : 0;

    if (+sp[1] >= 0) {
      //for 1.234e+2 scenario
      return frontDigits > +sp[1] ? frontDigits - +sp[1] : 0;
    } else {
      //for 1.234e-2 scenario
      return frontDigits + +sp[1] * -1;
    }
  } else {
    sp = (num + '').split('.'); //for 1.23445 scenario

    return sp.length >= 2 ? sp[1].length : 0;
  }
}

function isNumber(value) {
  return typeof value === 'number';
}

export { numberWithDecimalStyle, numberWithCurrencyStyle, numberWithPercentStyle, numberWithStyleEx, numberWithCustomFractionDigits, getFileSize, deLocalizeNumber, unformatNumber };